import { useMemo } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { Download, Loader2 } from "lucide-react";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { GameRuleGeneratedDocumentRow } from "../../types/gameRuleGeneratedDocument";
import { createDataTablePagination } from "./DataTablePagination";
import { withScrollableDataTable } from "./withScrollableDataTable";
import { formatDisplayDateTime } from "../../utils/displayDate";
import {
  gameRuleGeneratedDocumentStatusBadgeClass,
  gameRuleGeneratedDocumentStatusLabel,
} from "../../utils/gameRuleGeneratedDocumentStatus";

const pagination = createDataTablePagination({
  rowsPerPageOptions: [10, 20, 30, 50],
});
const ScrollableDataTable = withScrollableDataTable(DataTable);

const StatusBadge = ({ status }: { readonly status: string }) => {
  const styles = gameRuleGeneratedDocumentStatusBadgeClass(status);
  const label = gameRuleGeneratedDocumentStatusLabel(status);

  return (
    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${styles}`}>
      {label}
    </span>
  );
};

type GameRuleGeneratedDocumentsTableProps = {
  readonly rows: readonly GameRuleGeneratedDocumentRow[];
  readonly totalRows: number;
  readonly isLoading: boolean;
  readonly page: number;
  readonly perPage: number;
  readonly onChangePage: (page: number) => void;
  readonly onChangeRowsPerPage: (perPage: number) => void;
  readonly onDownload: (row: GameRuleGeneratedDocumentRow) => void | Promise<void>;
  /** Row whose download is in progress; shows spinner on that row only. */
  readonly downloadingRowId?: string | null;
};

export const GameRuleGeneratedDocumentsTable = ({
  rows,
  totalRows,
  isLoading,
  page,
  perPage,
  onChangePage,
  onChangeRowsPerPage,
  onDownload,
  downloadingRowId = null,
}: GameRuleGeneratedDocumentsTableProps) => {
  const columns: TableColumn<GameRuleGeneratedDocumentRow>[] = useMemo(
    () => [
      {
        id: "name",
        name: "Name",
        selector: (row) => row.name,
        sortable: false,
        grow: 2,
        cell: (row) => (
          <span className="line-clamp-2 font-medium text-gray-900" title={row.name}>
            {row.name}
          </span>
        ),
      },
      {
        id: "file_name",
        name: "File Name",
        selector: (row) => row.fileName,
        sortable: false,
        grow: 1,
        cell: (row) => (
          <span className="line-clamp-2 text-sm text-gray-700" title={row.fileName}>
            {row.fileName}
          </span>
        ),
      },
      {
        id: "generated_at",
        name: "Generated At",
        selector: (row) => row.generatedAt,
        sortable: false,
        width: "200px",
        cell: (row) => (
          <span className="text-sm tabular-nums text-gray-700">
            {formatDisplayDateTime(row.generatedAt)}
          </span>
        ),
      },
      {
        id: "section_count",
        name: "Section Count",
        selector: (row) => row.sectionCount,
        sortable: false,
        width: "150px",
        cell: (row) => (
          <span className="text-sm tabular-nums text-gray-700">{row.sectionCount}</span>
        ),
      },
      {
        id: "reviewer_email",
        name: "Reviewer Email",
        selector: (row) => row.reviewerEmail ?? "",
        sortable: false,
        grow: 1,
        cell: (row) => (
          <span className="truncate text-sm text-gray-700" title={row.reviewerEmail ?? undefined}>
            {row.reviewerEmail?.trim() || "—"}
          </span>
        ),
      },
      {
        id: "status",
        name: "Status",
        selector: (row) => row.status,
        sortable: false,
        width: "150px",
        cell: (row) => <StatusBadge status={row.status} />,
      },
      {
        id: "actions",
        name: "Action",
        sortable: false,
        width: "100px",
        right: true,
        cell: (row) => {
          const downloadInProgress = downloadingRowId !== null;
          const isThisRowDownloading = row.id === downloadingRowId;
          const downloadDisabled = downloadInProgress && !isThisRowDownloading;
          return (
            <div className="flex items-center justify-end">
              <button
                type="button"
                title={isThisRowDownloading ? "Downloading…" : "Download DOCX"}
                disabled={downloadDisabled}
                aria-busy={isThisRowDownloading}
                onClick={() => void onDownload(row)}
                className="inline-flex min-h-[34px] min-w-[34px] items-center justify-center rounded p-1.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-35 aria-busy:cursor-wait aria-busy:opacity-100"
              >
                {isThisRowDownloading ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary-600" aria-hidden />
                ) : (
                  <Download className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
          );
        },
      },
    ],
    [downloadingRowId, onDownload],
  );

  return (
    <div className="min-h-0 w-full flex-1 overflow-hidden">
      <ScrollableDataTable
        columns={columns}
        data={[...rows]}
        customStyles={dataTableCustomStyles}
        striped
        progressPending={isLoading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={perPage}
        paginationDefaultPage={page}
        paginationRowsPerPageOptions={[10, 20, 30, 50]}
        paginationComponent={pagination}
        onChangePage={onChangePage}
        onChangeRowsPerPage={(next) => onChangeRowsPerPage(next)}
        keyField="id"
        noDataComponent={
          <div className="p-12 text-center">
            <p className="text-sm text-gray-500">
              No generated game rules yet. Create one from the Word add-in (PrizeLogic).
            </p>
          </div>
        }
        highlightOnHover
        responsive
      />
    </div>
  );
};
